import { Icon, Skeleton } from '@vault/ui';
import type { Sweep } from '@vault/shared';
import { ramFreed, sweepsThisWeek } from './helpers.js';

export interface StatsRailProps {
  tabsSaved: number | null;
  dupCount: number;
  loading: boolean;
  sweeps: Sweep[];
  onReviewDupes: () => void;
}

/** Right-hand stats rail: totals, sweeps-this-week chart, duplicates callout. */
export function StatsRail({ tabsSaved, dupCount, loading, sweeps, onReviewDupes }: StatsRailProps) {
  const week = sweepsThisWeek(sweeps);
  const max = Math.max(1, ...week.map((b) => b.count));

  return (
    <>
      {/* totals */}
      <div style={cardStyle}>
        <div>
          <div style={{ fontFamily: 'var(--font-display-cond)', fontSize: 30, fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase', lineHeight: 1 }}>
            {loading || tabsSaved === null ? <Skeleton width={110} height={28} /> : tabsSaved.toLocaleString()}
          </div>
          <div style={eyebrowStyle}>TABS SAVED</div>
        </div>
        <div style={{ height: 1, background: 'var(--line)' }} />
        <div style={{ display: 'flex', gap: 18 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--font-display-cond)', fontSize: 19, fontWeight: 700, letterSpacing: '.01em', color: 'var(--amber-ink)' }}>
              {tabsSaved === null ? '—' : ramFreed(tabsSaved)}
            </div>
            <div style={{ ...eyebrowStyle, marginTop: 4 }}>RAM FREED</div>
          </div>
          <div style={{ width: 1, background: 'var(--line)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--font-display-cond)', fontSize: 19, fontWeight: 700, letterSpacing: '.01em' }}>
              {loading ? '—' : dupCount.toLocaleString()}
            </div>
            <div style={{ ...eyebrowStyle, marginTop: 4 }}>DUPLICATES</div>
          </div>
        </div>
      </div>

      {/* sweeps this week */}
      <div style={cardStyle}>
        <span style={eyebrowStyle}>SWEEPS THIS WEEK</span>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 7, height: 64 }}>
          {week.map((b, i) => {
            const h = Math.max(8, Math.round((b.count / max) * 100));
            const strong = b.today || (b.count === max && b.count > 0);
            return (
              <span
                key={i}
                title={`${b.count} ${b.count === 1 ? 'sweep' : 'sweeps'}`}
                style={{ flex: 1, height: `${h}%`, borderRadius: '4px 4px 2px 2px', background: strong ? 'var(--primary)' : 'var(--surface-3)' }}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 8.5, color: 'var(--text-3)' }}>
          {week.map((b, i) => (
            <span key={i} style={{ flex: 1, textAlign: 'center', color: b.today ? 'var(--text-1)' : 'var(--text-3)' }}>
              {b.label}
            </span>
          ))}
        </div>
      </div>

      {/* duplicates callout */}
      {dupCount > 0 && (
        <div style={{ background: 'var(--amber-tint)', border: '1px solid var(--amber-line)', borderRadius: 'var(--r-lg)', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: 'var(--amber-ink)' }}>
            <Icon name="layers" size={14} color="currentColor" />
            {dupCount} {dupCount === 1 ? 'duplicate' : 'duplicates'} found
          </span>
          <span style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-2)' }}>
            Open across your browsers right now. Close the extras to free memory.
          </span>
          <span onClick={onReviewDupes} style={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 600, color: 'var(--amber-ink)', cursor: 'pointer' }}>
            Review →
          </span>
        </div>
      )}
    </>
  );
}

const cardStyle: React.CSSProperties = {
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-lg)',
  boxShadow: 'var(--shadow-card)',
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

const eyebrowStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9.5,
  letterSpacing: '.14em',
  color: 'var(--text-3)',
};
